import { useState, useCallback } from "react";
import { useOrgSettings } from "@/lib/org-settings-context";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StatusFilter, type StatusOption } from "@/components/status-filter";
import { CreateCustomerDialog, LoadWalletDialog } from "@/components/dialogs";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import { DetailCard, DetailGrid, DetailItem } from "@/components/detail-page";
import { ActivityTab } from "@/components/activity-tab";
import { ImpersonateButton } from "@/components/impersonate-button";
import { IssueAccessCodeButton } from "@/components/issue-access-code-button";
import { ResetCustomerPinButton } from "@/components/reset-customer-pin-button";
import { EntityDrawer, useDrawerEntity, useDrawerSelect } from "@/components/entity-drawer";
import { VideoKycDialog } from "@/components/video-kyc-dialog";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  Plus,
  Users,
  Search,
  CreditCard,
  Wallet,
  Receipt,
  CheckCircle,
  XCircle,
  Pencil,
  Trash2,
  Loader2,
  Shield,
  ArrowUpCircle,
  Bot,
} from "lucide-react";

interface Customer {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  phone: string;
  dateOfBirth: string | null;
  address: string | null;
  addressLine1: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  kycStatus: string;
  kycTier: number | null;
  kycVerifiedAt: string | null;
  status: string;
  businessUnitId: string;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
}

interface VirtualCard {
  id: string;
  lastFour: string | null;
  status: string;
  cardType: string;
  nickname: string | null;
  dailyLimitCents: number | null;
  monthlyLimitCents: number | null;
  expMonth: number | null;
  expYear: number | null;
}

interface WalletData {
  id: string;
  balanceCents: number;
  currency: string;
  status: string;
}

function getStatusColor(status: string): "default" | "secondary" | "destructive" | "outline" {
  const statusColors: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    SUCCESS: "default",
    COMPLETED: "default",
    PENDING: "outline",
    AWAITING_PAYMENT: "outline",
    INITIATED: "secondary",
    FAILED: "destructive",
    EXPIRED: "secondary",
    REFUNDED: "secondary",
  };
  return statusColors[status] || "secondary";
}

function getKycBadge(status: string) {
  const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    VERIFIED: "default",
    PENDING_REVIEW: "outline",
    IN_PROGRESS: "secondary",
    REJECTED: "destructive",
    NOT_STARTED: "secondary",
  };
  const colors: Record<string, string> = {
    VERIFIED: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    PENDING_REVIEW: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  };
  return <Badge variant={variants[status] || "secondary"} className={colors[status]}>{status.replace("_", " ")}</Badge>;
}

function getCustomerStatusBadge(status: string) {
  const colors: Record<string, string> = {
    ACTIVE: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    INACTIVE: "",
    SUSPENDED: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  };
  return <Badge variant={status === "ACTIVE" ? "default" : "secondary"} className={colors[status]}>{status}</Badge>;
}

const KYC_STATUS_OPTIONS: StatusOption[] = [
  { value: "VERIFIED", label: "Verified" },
  { value: "PENDING_REVIEW", label: "Pending Review" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "NOT_STARTED", label: "Not Started" },
  { value: "REJECTED", label: "Rejected" },
];

// ─── Customer Detail Content (drawer) ────────────────────────────────────────

function CustomerDetailContent({ customerId, onClose }: { customerId: string; onClose: () => void }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const { formatCurrency } = useOrgSettings();
  const [, setLocation] = useLocation();
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Customer>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [loadWalletOpen, setLoadWalletOpen] = useState(false);
  const [videoKycOpen, setVideoKycOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [approveTier, setApproveTier] = useState("1");

  // Single composite query replaces 4 separate API calls
  const { data: detail, isLoading } = useQuery<{
    customer: Customer;
    cards: VirtualCard[];
    wallets: WalletData;
    topups: any[];
    counts: { cards: number; wallets: number; topups: number };
  }>({
    queryKey: ["customer-detail", customerId],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/customers/${customerId}/detail`);
      if (!response.ok) throw new Error("Failed to fetch customer details");
      return response.json();
    },
    enabled: !!customerId,
  });

  const customer = detail?.customer;
  const customerCards = detail?.cards;
  const wallet = detail?.wallets;
  const topups = detail?.topups;

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<Customer>) => {
      const response = await apiRequest("PATCH", `/api/customers/${customerId}`, updates);
      if (!response.ok) throw new Error("Failed to update customer");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-detail", customerId] });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      setIsEditing(false);
      toast({ title: "Success", description: "Customer updated successfully" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update customer", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("DELETE", `/api/customers/${customerId}`);
      if (!response.ok) throw new Error("Failed to delete customer");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      toast({ title: "Success", description: "Customer deleted successfully" });
      onClose();
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete customer", variant: "destructive" });
    },
  });

  const syncCustomerMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/admin/automation/sync-customer/${customerId}`);
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(typeof err.error === "string" ? err.error : "Sync failed");
      }
      return response.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["customer-detail", customerId] });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      toast({
        title: "Customer synced",
        description: `Updated ${data.fields_updated?.length || 0} fields from iServeU portal`,
      });
    },
    onError: (error: Error) => {
      toast({ title: "Sync failed", description: error.message, variant: "destructive" });
    },
  });

  const approveKycMutation = useMutation({
    mutationFn: async (params: { tier: number; notes?: string }) => {
      const response = await apiRequest("POST", `/api/kyc/${customerId}/approve`, params);
      if (!response.ok) throw new Error("Failed to approve KYC");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-detail", customerId] });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      toast({ title: "Success", description: "KYC approved" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to approve KYC", variant: "destructive" });
    },
  });

  const rejectKycMutation = useMutation({
    mutationFn: async (reason: string) => {
      const response = await apiRequest("POST", `/api/kyc/${customerId}/reject`, { reason });
      if (!response.ok) throw new Error("Failed to reject KYC");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-detail", customerId] });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      setRejectDialogOpen(false);
      setRejectReason("");
      toast({ title: "Success", description: "KYC rejected" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to reject KYC", variant: "destructive" });
    },
  });

  const handleEdit = () => {
    if (customer) {
      setEditForm({
        firstName: customer.firstName || "",
        lastName: customer.lastName || "",
        email: customer.email,
        phone: customer.phone,
        address: customer.address || "",
        city: customer.city || "",
        state: customer.state || "",
        postalCode: customer.postalCode || "",
        country: customer.country || "",
        status: customer.status,
      });
      setIsEditing(true);
    }
  };

  const handleSave = () => updateMutation.mutate(editForm);
  const handleCancelEdit = () => { setIsEditing(false); setEditForm({}); };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!customer) return null;

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Users className="h-5 w-5 text-muted-foreground" />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Customer</span>
        </div>
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h2 className="text-xl font-semibold">{customer.firstName} {customer.lastName}</h2>
            {getCustomerStatusBadge(customer.status)}
          </div>
          <p className="text-sm text-muted-foreground">{customer.email}</p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {!isEditing && (
            <>
              <ImpersonateButton
                entityType="CUSTOMER"
                entityId={customer.id}
                entityName={`${customer.firstName} ${customer.lastName}`}
                variant="outline"
                size="sm"
              />
              <IssueAccessCodeButton
                customerId={customer.id}
                customerName={`${customer.firstName ?? ""} ${customer.lastName ?? ""}`.trim()}
              />
              <ResetCustomerPinButton
                customerId={customer.id}
                customerName={`${customer.firstName ?? ""} ${customer.lastName ?? ""}`.trim()}
              />
              <Button variant="outline" size="sm" onClick={handleEdit}>
                <Pencil className="h-3.5 w-3.5 mr-1.5" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => setDeleteDialogOpen(true)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                )}
                Delete
              </Button>
              {wallet && (
                <Button size="sm" onClick={() => setLoadWalletOpen(true)}>
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  Load Wallet
                </Button>
              )}
            </>
          )}
        </div>

        <div className="border-t" />

        {isEditing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>First Name</Label>
                  <Input
                    value={editForm.firstName || ""}
                    onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Name</Label>
                  <Input
                    value={editForm.lastName || ""}
                    onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editForm.email || ""}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={editForm.phone || ""}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Input
                  value={editForm.address || ""}
                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    value={editForm.city || ""}
                    onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Input
                    value={editForm.state || ""}
                    onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>PIN Code</Label>
                  <Input
                    value={editForm.postalCode || ""}
                    onChange={(e) => setEditForm({ ...editForm, postalCode: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Country</Label>
                  <Input
                    value={editForm.country || ""}
                    onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={editForm.status}
                  onValueChange={(value) => setEditForm({ ...editForm, status: value })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="INACTIVE">Inactive</SelectItem>
                    <SelectItem value="SUSPENDED">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={handleCancelEdit}>Cancel</Button>
              <Button onClick={handleSave} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        ) : (
          <Tabs defaultValue="overview" className="space-y-4">
            <TabsList className="w-full justify-start">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="cards">Cards ({customerCards?.length || 0})</TabsTrigger>
              <TabsTrigger value="wallet">Wallet</TabsTrigger>
              <TabsTrigger value="topups">Top-ups ({topups?.length || 0})</TabsTrigger>
              <TabsTrigger value="activity">Activity</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4">
              <DetailCard title="Personal Information">
                <DetailGrid columns={1}>
                  <DetailItem label="First Name" value={customer.firstName} />
                  <DetailItem label="Last Name" value={customer.lastName} />
                  <DetailItem label="Email" value={customer.email} />
                  <DetailItem label="Phone" value={customer.phone} />
                  <DetailItem label="Date of Birth" value={customer.dateOfBirth ? new Date(customer.dateOfBirth).toLocaleDateString() : "-"} />
                  <DetailItem label="Status" value={getCustomerStatusBadge(customer.status)} />
                </DetailGrid>
              </DetailCard>

              <DetailCard
                title="Address"
                headerActions={user?.role === "SUPER" ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={syncCustomerMutation.isPending}
                    onClick={() => syncCustomerMutation.mutate()}
                  >
                    {syncCustomerMutation.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Bot className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    {syncCustomerMutation.isPending ? "Syncing..." : "Sync from iServeU"}
                  </Button>
                ) : undefined}
              >
                {syncCustomerMutation.isPending ? (
                  <div className="flex flex-col items-center justify-center py-4 text-center">
                    <Loader2 className="h-5 w-5 animate-spin text-primary mb-2" />
                    <p className="text-xs text-muted-foreground">Fetching address from iServeU portal... 1–3 min</p>
                  </div>
                ) : (
                  <DetailGrid columns={1}>
                    <DetailItem label="Street Address" value={customer.addressLine1 || customer.address || "–"} />
                    <DetailItem label="City" value={customer.city || "–"} />
                    <DetailItem label="State" value={customer.state || "–"} />
                    <DetailItem label="PIN Code" value={customer.postalCode || "–"} />
                    <DetailItem label="Country" value={customer.country || "–"} />
                  </DetailGrid>
                )}
              </DetailCard>

              <DetailCard
                title="KYC Verification"
                headerActions={
                  <div className="flex gap-2">
                    {customer.kycStatus === "PENDING_REVIEW" && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setRejectDialogOpen(true)}
                          disabled={rejectKycMutation.isPending}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => approveKycMutation.mutate({ tier: parseInt(approveTier), notes: "Approved via admin panel" })}
                          disabled={approveKycMutation.isPending}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                      </>
                    )}
                  </div>
                }
              >
                <DetailGrid columns={1}>
                  <DetailItem label="KYC Status" value={getKycBadge(customer.kycStatus)} />
                  <DetailItem label="KYC Tier" value={customer.kycTier?.toString() || "-"} />
                  <DetailItem label="Verified At" value={customer.kycVerifiedAt ? new Date(customer.kycVerifiedAt).toLocaleString() : "-"} />
                </DetailGrid>

                {/* KYC Action Buttons */}
                {customer.kycStatus !== "VERIFIED" && (
                  <div className="flex gap-2 mt-4 pt-4 border-t">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setVideoKycOpen(true)}
                    >
                      <Shield className="h-4 w-4 mr-1" />
                      Start KYC
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled
                    >
                      <ArrowUpCircle className="h-4 w-4 mr-1" />
                      Upgrade KYC
                      <Badge variant="outline" className="ml-1 text-[10px] px-1.5 py-0 bg-amber-100 text-amber-700 border-amber-300">Coming Soon</Badge>
                    </Button>
                  </div>
                )}
              </DetailCard>

              {/* KYC Dialog */}
              <VideoKycDialog
                open={videoKycOpen}
                onOpenChange={setVideoKycOpen}
                customerId={customerId}
                customerName={customer.firstName && customer.lastName ? `${customer.firstName} ${customer.lastName}` : undefined}
                customerEmail={customer.email}
                customerPhone={customer.phone}
                onSuccess={() => {
                  queryClient.invalidateQueries({ queryKey: ["customer-detail", customerId] });
                }}
              />

              {/* Reject KYC Dialog */}
              <AlertDialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Reject KYC</AlertDialogTitle>
                    <AlertDialogDescription>
                      Please provide a reason for rejecting this customer's KYC verification.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <div className="py-2">
                    <Textarea
                      placeholder="Enter rejection reason..."
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      rows={3}
                    />
                  </div>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => rejectKycMutation.mutate(rejectReason)}
                      disabled={!rejectReason.trim() || rejectKycMutation.isPending}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Reject KYC
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <DetailCard title="Record Information">
                <DetailGrid columns={1}>
                  <DetailItem label="Customer ID" value={<code className="text-xs bg-muted px-2 py-1 rounded break-all">{customer.id}</code>} />
                  <DetailItem label="Created" value={new Date(customer.createdAt).toLocaleString()} />
                  <DetailItem label="Last Updated" value={new Date(customer.updatedAt).toLocaleString()} />
                </DetailGrid>
              </DetailCard>
            </TabsContent>

            <TabsContent value="cards">
              <DetailCard title="Virtual Cards" description="Cards issued to this customer">
                {customerCards && customerCards.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Card</TableHead>
                          <TableHead>Nickname</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Expiry</TableHead>
                          <TableHead>Daily Limit</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {customerCards.map((card) => (
                          <TableRow
                            key={card.id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => setLocation(`/cards?select=${card.id}`)}
                          >
                            <TableCell className="font-mono">
                              <div className="flex items-center gap-2">
                                <CreditCard className="h-4 w-4 text-muted-foreground" />
                                **** {card.lastFour || "****"}
                              </div>
                            </TableCell>
                            <TableCell>{card.nickname || "-"}</TableCell>
                            <TableCell><Badge variant="outline">{card.cardType}</Badge></TableCell>
                            <TableCell>
                              {card.expMonth && card.expYear ? `${card.expMonth}/${card.expYear}` : "-"}
                            </TableCell>
                            <TableCell>{formatCurrency(card.dailyLimitCents)}</TableCell>
                            <TableCell>
                              <Badge variant={card.status === "ACTIVE" ? "default" : "secondary"}>
                                {card.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CreditCard className="mx-auto h-12 w-12 text-muted-foreground" />
                    <p className="mt-2 text-muted-foreground">No cards issued</p>
                  </div>
                )}
              </DetailCard>
            </TabsContent>

            <TabsContent value="wallet">
              <DetailCard
                title="Wallet Balance"
                headerActions={
                  wallet && (
                    <Button size="sm" onClick={() => setLoadWalletOpen(true)}>
                      <Plus className="h-4 w-4 mr-1" />
                      Load Funds
                    </Button>
                  )
                }
              >
                {wallet ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                      <Wallet className="h-8 w-8 text-primary" />
                      <div>
                        <p className="text-sm text-muted-foreground">Available Balance</p>
                        <p className="text-3xl font-bold">{formatCurrency(wallet.balanceCents)}</p>
                      </div>
                      <Badge className="ml-auto" variant={wallet.status === "ACTIVE" ? "default" : "secondary"}>
                        {wallet.status}
                      </Badge>
                    </div>
                    <DetailGrid columns={1}>
                      <DetailItem
                        label="Wallet ID"
                        value={
                          <Button
                            variant="link"
                            className="p-0 h-auto font-mono text-xs"
                            onClick={() => setLocation(`/wallets?select=${wallet.id}`)}
                          >
                            {wallet.id}
                          </Button>
                        }
                      />
                      <DetailItem label="Currency" value={wallet.currency} />
                    </DetailGrid>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Wallet className="mx-auto h-12 w-12 text-muted-foreground" />
                    <p className="mt-2 text-muted-foreground">No wallet found</p>
                  </div>
                )}
              </DetailCard>
            </TabsContent>

            <TabsContent value="topups">
              <DetailCard title="PayHub Top-ups" description="Payment history from PayHub">
                {topups && topups.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Transaction ID</TableHead>
                          <TableHead>Order ID</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Source</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {topups.map((topup) => (
                          <TableRow key={topup.id}>
                            <TableCell className="text-sm">
                              {topup.createdAt ? new Date(topup.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : "-"}
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {topup.payhubTransactionId || "-"}
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {topup.payhubTopUpId || "-"}
                            </TableCell>
                            <TableCell className="font-semibold">
                              {formatCurrency(topup.amountPaise)}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{topup.sourceType || "UNKNOWN"}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant={getStatusColor(topup.status || "PENDING")}>
                                {topup.status || "PENDING"}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Receipt className="mx-auto h-12 w-12 text-muted-foreground" />
                    <p className="mt-2 text-muted-foreground">No top-up transactions</p>
                  </div>
                )}
              </DetailCard>
            </TabsContent>

            <TabsContent value="activity">
              <ActivityTab
                entityType="USER"
                entityId={customer.id}
                createdAt={customer.createdAt}
                updatedAt={customer.updatedAt}
              />
            </TabsContent>
          </Tabs>
        )}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Customer</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{customer.firstName} {customer.lastName}&quot;? This will also delete all associated cards and transactions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteMutation.mutate()} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <LoadWalletDialog
        open={loadWalletOpen}
        onOpenChange={setLoadWalletOpen}
        customerId={customer.id}
        customerName={`${customer.firstName} ${customer.lastName}`}
      />
    </>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function CustomerView() {
  const { user } = useAuth();
  const { formatCurrency } = useOrgSettings();
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const [searchQuery, setSearchQuery] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const { drawerEntity, setDrawerEntity, displayEntity } = useDrawerEntity<string>();
  const canCreate = user?.role === "SUPER" || user?.role === "ORGANIZATION" || user?.role === "BUSINESS_UNIT";

  // Support ?select= for cross-entity navigation
  useDrawerSelect(setDrawerEntity);

  // Parse status filter from URL
  const searchParams = new URLSearchParams(searchString);
  const urlStatus = searchParams.get("status");
  const [statusFilter, setStatusFilter] = useState<string | null>(urlStatus);

  const { data: customers, isLoading } = useQuery<Customer[]>({
    queryKey: ["customers"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/customers");
      if (!response.ok) throw new Error("Failed to fetch customers");
      return response.json();
    },
  });

  // Handle status filter change with URL sync
  const handleStatusChange = (status: string | null) => {
    setStatusFilter(status);
    const newParams = new URLSearchParams(searchString);
    if (status) {
      newParams.set("status", status);
    } else {
      newParams.delete("status");
    }
    newParams.delete("select");
    const newSearch = newParams.toString();
    setLocation(newSearch ? `?${newSearch}` : "", { replace: true });
  };

  const handleRowClick = useCallback((customerId: string) => {
    setDrawerEntity(prev => prev === customerId ? null : customerId);
  }, [setDrawerEntity]);

  // Filter customers by search and KYC status
  const q = searchQuery.toLowerCase();
  const filteredCustomers = customers?.filter((c) => {
    const name = `${c.firstName ?? ""} ${c.lastName ?? ""}`.toLowerCase();
    const matchesSearch =
      name.includes(q) ||
      (c.email ?? "").toLowerCase().includes(q) ||
      (c.phone ?? "").includes(searchQuery);
    const matchesStatus = !statusFilter || c.kycStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Count by KYC status for filter badges
  const statusCounts = customers?.reduce(
    (acc, c) => {
      acc[c.kycStatus] = (acc[c.kycStatus] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const statusOptionsWithCounts: StatusOption[] = KYC_STATUS_OPTIONS.map((opt) => ({
    ...opt,
    count: statusCounts?.[opt.value] || 0,
  }));

  return (
    <div className="relative min-h-screen">
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-display font-bold tracking-tight">Customers</h1>
            <p className="text-muted-foreground">Manage cardholders</p>
          </div>
          {canCreate && (
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Customer
            </Button>
          )}
        </div>

        {/* KYC Status Filters */}
        <StatusFilter
          value={statusFilter}
          onChange={handleStatusChange}
          options={statusOptionsWithCounts}
        />

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>
                  {statusFilter ? `${statusFilter.replace("_", " ")} Customers` : "All Customers"}
                </CardTitle>
                <CardDescription>
                  {filteredCustomers?.length ?? 0} customer{(filteredCustomers?.length ?? 0) !== 1 ? "s" : ""} found
                </CardDescription>
              </div>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : filteredCustomers?.length === 0 ? (
              <div className="text-center py-12">
                <Users className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-lg font-semibold">No customers</h3>
                <p className="text-muted-foreground">
                  {statusFilter
                    ? `No ${statusFilter.replace("_", " ").toLowerCase()} customers found.`
                    : "Create your first cardholder."}
                </p>
                {!statusFilter && canCreate && (
                  <Button className="mt-4" onClick={() => setCreateDialogOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Customer
                  </Button>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>KYC Status</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCustomers?.map((customer) => (
                    <TableRow
                      key={customer.id}
                      className={cn(
                        "cursor-pointer hover:bg-muted/50",
                        drawerEntity === customer.id && "bg-primary/10 hover:bg-primary/15"
                      )}
                      onClick={() => handleRowClick(customer.id)}
                    >
                      <TableCell className="font-medium">{customer.firstName} {customer.lastName}</TableCell>
                      <TableCell>{customer.email}</TableCell>
                      <TableCell>{customer.phone}</TableCell>
                      <TableCell>{getKycBadge(customer.kycStatus)}</TableCell>
                      <TableCell>{getCustomerStatusBadge(customer.status)}</TableCell>
                      <TableCell>{new Date(customer.createdAt).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <CreateCustomerDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
        />
      </div>

      {/* Right-side detail drawer */}
      <EntityDrawer isOpen={!!drawerEntity} onClose={() => setDrawerEntity(null)}>
        {displayEntity && (
          <CustomerDetailContent key={displayEntity} customerId={displayEntity} onClose={() => setDrawerEntity(null)} />
        )}
      </EntityDrawer>
    </div>
  );
}
